import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import VendorReviewForm from './VendorReviewForm';
import { vendorApi } from '../services/api';
import { VendorPublicProfile } from '../types';
import { extractApiError } from '../utils/errorUtils';

/**
 * VendorReviewPage (Issue #956)
 *
 * Page for submitting a vendor review.
 * Requires vendorId from URL params and fairId from query string.
 */

export default function VendorReviewPage() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState<VendorPublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fairId = searchParams.get('fair_id');

  useEffect(() => {
    let isMounted = true;

    const loadVendor = async () => {
      if (!vendorId) {
        if (isMounted) {
          setError('Invalid vendor ID');
          setLoading(false);
        }
        return;
      }

      const id = parseInt(vendorId, 10);
      if (isNaN(id)) {
        if (isMounted) {
          setError('Invalid vendor ID');
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await vendorApi.getVendorPublicProfile(id);
        if (isMounted) {
          setVendor(data);
        }
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load vendor:', apiError);
        if (isMounted) {
          setError(apiError.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadVendor();

    return () => {
      isMounted = false;
    };
  }, [vendorId]);

  const handleSuccess = () => {
    // Navigate back to vendor profile after successful review submission
    setTimeout(() => {
      navigate(`/vendors/${vendorId}#reviews`);
    }, 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-800">{error || 'Vendor not found'}</p>
            <Link
              to="/vendors"
              className="inline-flex items-center gap-2 mt-4 text-secondary hover:text-primary-dark"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Vendors
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!fairId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <p className="text-yellow-800">Missing fair ID. Please select a fair to review.</p>
            <Link
              to={`/vendors/${vendorId}`}
              className="inline-flex items-center gap-2 mt-4 text-secondary hover:text-primary-dark"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Vendor Profile
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" data-testid="vendor-review-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={`/vendors/${vendorId}`}
            className="inline-flex items-center gap-2 text-secondary hover:text-primary-dark mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {vendor.business_name}
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Write a Review</h1>
          <p className="text-gray-600 mt-2">
            Share your experience with <span className="font-semibold">{vendor.business_name}</span>
          </p>
        </div>

        {/* Review Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <VendorReviewForm
            vendorId={parseInt(vendorId, 10)}
            fairId={parseInt(fairId, 10)}
            onSuccess={handleSuccess}
          />
        </div>
      </div>
    </div>
  );
}
